import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { presentationService } from '../../services/api';
import { useLanguage } from '../../context/LanguageContext';
import cd1 from '../../assets/cd1.png';
import cd2 from '../../assets/cd2.png';
import cd3 from '../../assets/cd3.png';
import cd4 from '../../assets/cd4.png';
import mobileCardBg from '../../assets/mobile-card-background.png';

// Desktop kart component'i
const DesktopCard = ({ card, index, language, onNavigate }) => {
    const cardRef = useRef(null);
    const isInView = useInView(cardRef, {
        once: true,
        amount: 0.3,
        margin: "0px 0px -50px 0px"
    });

    const particleVariants = {
        animate: (i) => ({
            y: [0, -15, 0],
            x: [0, i % 2 === 0 ? 10 : -10, 0],
            opacity: [0, 1, 0],
            scale: [0, 1, 0],
            transition: {
                duration: 3,
                repeat: Infinity,
                delay: i * 0.5,
                ease: "easeInOut"
            }
        })
    };

    return (
        <motion.div
            ref={cardRef}
            className={`relative flex ${card.marginClass} ${card.direction === 'rtl' ? 'flex-row-reverse' : ''} rounded-2xl px-9 justify-between py-2 overflow-visible group`}
            style={{
                backgroundImage: `url(${card.bgImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                height: '317px',
                width: '100%',
                minWidth: '1051px'
            }}
            initial={{
                opacity: 0,
                x: card.direction === 'ltr' ? 200 : -200,
                rotateY: card.direction === 'ltr' ? 15 : -15,
                scale: 0.85,
                filter: 'blur(8px)',
            }}
            animate={isInView ? {
                opacity: 1,
                x: 0,
                rotateY: 0,
                scale: 1,
                filter: 'blur(0px)',
            } : {}}
            transition={{
                duration: 0.9,
                ease: [0.25, 0.46, 0.45, 0.94],
                opacity: { duration: 0.7 },
                filter: { duration: 0.8 },
            }}
            whileHover={{
                scale: 1.02,
                transition: { duration: 0.3 }
            }}
        >
            <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent rounded-2xl pointer-events-none"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
            />

            {[...Array(4)].map((_, i) => (
                <motion.div
                    key={i}
                    custom={i}
                    variants={particleVariants}
                    animate={isInView ? "animate" : ""}
                    className="absolute w-2 h-2 bg-[#FFCD7C] rounded-full pointer-events-none"
                    style={{
                        top: `${15 + i * 25}%`,
                        left: `${8 + i * 12}%`,
                    }}
                />
            ))}

            <motion.img
                src={card.contentImage}
                alt=""
                className={`w-auto h-auto ${card.direction === 'ltr' ? 'mr-0' : ''}`}
                initial={{ scale: 0.8, opacity: 0, rotate: card.direction === 'ltr' ? -10 : 10 }}
                animate={isInView ? {
                    scale: 1,
                    opacity: 1,
                    rotate: 0,
                    transition: { duration: 0.7, delay: 0.3, ease: "easeOut" }
                } : {}}
                whileHover={{
                    scale: 1.05,
                    rotate: 0,
                    transition: { duration: 0.3 }
                }}
            />

            <div className='w-[40%] relative'>
                <motion.p
                    className="text-[#FFCD7C] font-extrabold text-[42px] text-right leading-tight mt-8"
                    initial={{ opacity: 0, x: card.direction === 'rtl' ? -60 : 60 }}
                    animate={isInView ? {
                        opacity: 1,
                        x: 0,
                        transition: { duration: 0.6, delay: 0.4, ease: "easeOut" }
                    } : {}}
                >
                    {card.title}
                </motion.p>

                <motion.p
                    className='text-white font-light text-xl'
                    initial={{ opacity: 0 }}
                    animate={isInView ? {
                        opacity: 1,
                        transition: { duration: 0.4, delay: 0.5, ease: "easeOut" }
                    } : {}}
                >
                    {card.subtitle}
                </motion.p>

                <motion.p
                    className='text-justify text-white font-bold text-xl w-[400px] mt-2'
                    initial={{ opacity: 0, y: 20 }}
                    animate={isInView ? {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.5, delay: 0.6, ease: "easeOut" }
                    } : {}}
                >
                    {card.summary}
                </motion.p>

                <div className={`flex ${card.justifyClass}`}>
                    <motion.button
                        onClick={() => onNavigate(card.slug)}
                        className={`px-4 py-1 rounded-lg absolute bottom-0 ${card.direction === 'ltr' ? 'left-0' : 'right-0'} text-[#000377] font-bold text-xl shadow-lg 
                            bg-gradient-to-b from-[#ffdfac] via-[#F7BD5F] to-[#f5b856]
                            hover:bg-gradient-to-b hover:from-gray-300 hover:via-gray-400 hover:to-gray-500
                            transition-all cursor-pointer`}
                        initial={{ opacity: 0 }}
                        animate={isInView ? {
                            opacity: 1,
                            y: 0,
                            transition: { duration: 0.5, delay: 0.6, ease: "easeOut" }
                        } : {}}
                        whileHover={{
                            scale: 1.05,
                            transition: { duration: 0.3 }
                        }}
                        whileTap={{ scale: 0.95 }}
                    >
                        {language === 'fa' ? 'مشاهده رزومه' : 'View Resume'}
                    </motion.button>
                </div>
            </div>
        </motion.div>
    );
};

// Mobil kart component'i - artik tamamen dynamic, API datasini kullaniyor
const MobileCard = ({ card, index, language, onNavigate }) => {
    const cardRef = useRef(null);
    const isInView = useInView(cardRef, {
        once: true,
        amount: 0.3,
        margin: "0px 0px -30px 0px"
    });

    // Tek sutun / cift sutun kartlarina farkli giris yonu (desktop'taki ltr/rtl mantiginin mobile karsiligi)
    const direction = index % 2 === 0 ? -1 : 1;

    const particleVariants = {
        animate: (i) => ({
            y: [0, -10, 0],
            x: [0, i % 2 === 0 ? 6 : -6, 0],
            opacity: [0, 1, 0],
            scale: [0, 1, 0],
            transition: {
                duration: 2.5,
                repeat: Infinity,
                delay: i * 0.4,
                ease: "easeInOut"
            }
        })
    };

    return (
        <motion.div
            ref={cardRef}
            onClick={() => onNavigate(card.slug)}
            className="relative w-[48%] sm:w-auto max-w-[200px] cursor-pointer group rounded-xl"
            style={{
                backgroundImage: `url(${mobileCardBg})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                width: '185px',
                height: '226px',
            }}
            initial={{
                opacity: 0,
                x: direction * 80,
                rotateY: direction * 20,
                scale: 0.7,
                filter: 'blur(4px)',
            }}
            animate={isInView ? {
                opacity: 1,
                x: 0,
                rotateY: 0,
                scale: 1,
                filter: 'blur(0px)',
            } : {}}
            transition={{
                duration: 0.7,
                ease: [0.25, 0.46, 0.45, 0.94],
                filter: { duration: 0.5 }
            }}
            whileHover={{
                scale: 1.04,
                transition: { duration: 0.3 }
            }}
            whileTap={{ scale: 0.96 }}
        >
            {/* Shine efekti - desktop ile ayni mantik */}
            <motion.div
                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent pointer-events-none z-10"
                initial={{ x: '-100%' }}
                whileHover={{ x: '100%' }}
                transition={{ duration: 1.2, ease: "easeInOut" }}
            />

            {/* Parlayan partikuller */}
            {[...Array(2)].map((_, i) => (
                <motion.div
                    key={i}
                    custom={i}
                    variants={particleVariants}
                    animate={isInView ? "animate" : ""}
                    className="absolute w-1.5 h-1.5 bg-[#FFCD7C] rounded-full pointer-events-none z-10"
                    style={{
                        top: `${18 + i * 15}%`,
                        right: `${12 + i * 18}%`,
                    }}
                />
            ))}

            {/* Foreground gorsel - sabit 177x138px, kadrajdan tasmis gorunum icin ust kisimda, absolute degil, normal akista */}
            <motion.img
                src={card.contentImage}
                alt={card.title}
                className="mx-auto relative z-[1] select-none pointer-events-none object-contain"
                style={{ width: '180px', height: '138px', marginTop: '-12px', marginRight: '-5px' }}
                initial={{ scale: 0.85, opacity: 0 }}
                animate={isInView ? {
                    scale: 1,
                    opacity: 1,
                    transition: { duration: 0.6, delay: 0.2, ease: "easeOut" }
                } : {}}
                whileHover={{
                    scale: 1.06,
                    transition: { duration: 0.3 }
                }}
            />

            {/* Metin alani */}
            <div className="relative z-[1] flex flex-col items-center text-center px-2 -mt-2">
                <motion.p
                    className="text-[#FFCD7C] font-extrabold leading-tight"
                    style={{ fontSize: '16px' }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.5, delay: 0.3, ease: "easeOut" }
                    } : {}}
                >
                    {card.title}
                </motion.p>

                <motion.p
                    className="text-white font-light leading-tight mt-0.5"
                    style={{ fontSize: '8px' }}
                    initial={{ opacity: 0 }}
                    animate={isInView ? {
                        opacity: 1,
                        transition: { duration: 0.4, delay: 0.4, ease: "easeOut" }
                    } : {}}
                >
                    {card.subtitle}
                </motion.p>

                <motion.p
                    className="text-white font-bold leading-tight mt-1 line-clamp-3"
                    style={{ fontSize: '10px' }}
                    initial={{ opacity: 0, y: 10 }}
                    animate={isInView ? {
                        opacity: 1,
                        y: 0,
                        transition: { duration: 0.5, delay: 0.5, ease: "easeOut" }
                    } : {}}
                >
                    {card.summary}
                </motion.p>
            </div>

            {/* Buton */}
            <div className="absolute -bottom-2 left-0 right-0 flex justify-center z-[1]">
                <motion.button
                    onClick={(e) => {
                        e.stopPropagation();
                        onNavigate(card.slug);
                    }}
                    className="px-3 py-1 rounded-md text-[#000377] font-bold text-[10px] shadow-lg
                        bg-gradient-to-b from-[#ffdfac] via-[#F7BD5F] to-[#f5b856]
                        hover:bg-gradient-to-b hover:from-gray-300 hover:via-gray-400 hover:to-gray-500
                        transition-all cursor-pointer"
                    initial={{ opacity: 0 }}
                    animate={isInView ? {
                        opacity: 1,
                        transition: { duration: 0.5, delay: 0.6, ease: "easeOut" }
                    } : {}}
                    whileHover={{
                        scale: 1.05,
                        transition: { duration: 0.3 }
                    }}
                    whileTap={{ scale: 0.95 }}
                >
                    {language === 'fa' ? 'مشاهده رزومه' : 'View Resume'}
                </motion.button>
            </div>
        </motion.div>
    );
};

const Cards = () => {
    const navigate = useNavigate();
    const { language } = useLanguage();
    const [services, setServices] = useState([]);

    useEffect(() => {
        fetchServices();
    }, [language]);

    const fetchServices = async () => {
        try {
            const data = await presentationService.getServices();
            const cards = (data || []).map((service, index) => ({
                id: index + 1,
                slug: service.slug, // Service sayfası için slug
                bgImage: service.background || [cd1, cd2, cd3, cd4][index % 4],
                contentImage: service.foreground || ["/cd1.png", "/cd2.png", "/cd3.png", "/cd4.png"][index % 4],
                title: service.title || (language === 'fa' ? 'غرفه‌سازی نمایشگاهی' : 'Exhibition Booth'),
                subtitle: service.subtitle || '',
                summary: service.summary || '',
                marginClass: index % 2 === 0 ? '-ml-20' : '-mr-20',
                direction: index % 2 === 0 ? 'ltr' : 'rtl',
                justifyClass: 'justify-start',
            }));
            setServices(cards);
        } catch (err) {
            console.error('Services API error:', err);
        }
    };

    const handleNavigate = (slug) => {
        if (slug) {
            navigate(`/service/${slug}`);
        }
    };

    return (
        <div className="relative flex flex-col justify-center items-center mt-8 sm:mt-12 max-w-6xl w-full mx-auto px-3 sm:px-4 md:px-6">
            {/* Desktop Kartlar */}
            {services.length > 0 && (
                <div className="hidden md:block">
                    <div className="flex gap-8 justify-center items-center flex-col">
                        {services.map((card, index) => (
                            <DesktopCard
                                key={card.id}
                                card={card}
                                index={index}
                                language={language}
                                onNavigate={handleNavigate}
                            />
                        ))}
                    </div>
                </div>
            )}

            {/* Mobil Kartlar - artik API datasindan dinamik olarak geliyor */}
            {services.length > 0 && (
                <div className="block md:hidden w-full">
                    <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                        {services.map((card, index) => (
                            <div
                                key={card.id}
                                className={`${index % 2 === 1 ? 'mt-4' : ''}`} 
                            >
                                <MobileCard
                                    card={card}
                                    index={index}
                                    language={language}
                                    onNavigate={handleNavigate}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default Cards;